// Source https://github.com/shadcn-ui/ui/blob/main/apps/www/registry/default/ui/pagination.tsx
import { Button, type ButtonProps } from '@latitude/button';
import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import { cn } from '@latitude/token-utils';
import type { ComponentProps, Ref } from 'react';

const Pagination = ({ className, ...props }: ComponentProps<'nav'>) => (
  <nav aria-label="pagination" className={className} {...props} />
);
Pagination.displayName = 'Pagination';

const PaginationContent = ({
  className,
  ref,
  ...props
}: ComponentProps<'ul'> & {
  ref?: Ref<HTMLUListElement>;
}) => (
  <ul
    ref={ref}
    className={cn(
      'gap-spacing-comp-action-medium-gap flex flex-row items-center',
      className,
    )}
    {...props}
  />
);
PaginationContent.displayName = 'PaginationContent';

type PaginationLinkProps = ButtonProps;

const PaginationLink = (props: PaginationLinkProps) => (
  <Button variant="neutral" type="ghost" size="medium" {...props} />
);

PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  className,
  ...props
}: Omit<ComponentProps<typeof IconButton>, 'icon' | 'aria-label'>) => (
  <IconButton
    aria-label="Go to previous page"
    htmlType="button"
    variant="neutral"
    type="ghost"
    size="medium"
    className={className}
    icon={<Icon name="chevron-left" direction="left" />}
    {...props}
  />
);

PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
  className,
  ...props
}: Omit<ComponentProps<typeof IconButton>, 'icon' | 'aria-label'>) => (
  <IconButton
    aria-label="Go to next page"
    htmlType="button"
    variant="neutral"
    type="ghost"
    size="medium"
    className={className}
    icon={<Icon name="chevron-left" direction="right" />}
    {...props}
  />
);

PaginationNext.displayName = 'PaginationNext';

type SimplePaginationProps = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  className?: string;
  onNext: () => void;
  onPrevious: () => void;
  isLoading?: boolean;
};

const SimplePagination = ({
  hasNextPage,
  hasPreviousPage,
  onNext,
  onPrevious,
  className,
  isLoading,
}: SimplePaginationProps) => (
  <Pagination className={className}>
    <PaginationContent>
      <PaginationPrevious
        disabled={!hasPreviousPage || isLoading}
        onClick={onPrevious}
      />
      <PaginationNext disabled={!hasNextPage || isLoading} onClick={onNext} />
    </PaginationContent>
  </Pagination>
);

SimplePagination.displayName = 'SimplePagination';

export { SimplePagination as Pagination };
